/**
 * Confidentiality, IP and content templates — 9 fill-in-the-blank documents.
 *
 * Four different jobs are being done across this file, and the templates are written so a founder
 * can tell them apart:
 *   confidentiality — they cannot tell anyone        (one-way-nda, manufacturing-nda)
 *   non-use         — they cannot use it themselves  (manufacturing-nda, product-development-nda)
 *   ownership       — it is yours by assignment      (contractor-nda, ip-assignment-agreement,
 *                                                     work-for-hire-agreement)
 *   permission      — someone else owns it and you may use it (content-release, model-release)
 *
 * Every body uses visible [BRACKET] blanks. An unfilled blank stays on the page on purpose.
 * The renderer prepends the standard disclaimer; nothing here competes with it.
 */

export const CONFIDENTIALITY_TEMPLATES = [
  // mutual-nda is spliced verbatim from templates/_exemplar.js. Do not retype it; if the exemplar
  // changes, re-splice rather than editing this copy by hand.
{
  id: "mutual-nda",
  docId: "mutual-nda",
  title: "MUTUAL NON-DISCLOSURE AGREEMENT",
  version: "1.0",
  lastReviewed: "2026-08-25",

  founderGuide: {
    useThisWhen:
      "Both sides will share something confidential — you are describing your formula, roadmap or numbers, and they are describing their process, pricing or capabilities. Use this before the first substantive conversation with a co-packer, formulator, potential partner or acquirer.",
    whyItMatters:
      "In CPG the confidential thing is usually the formula, the supplier list or the retailer pipeline, and all three travel easily. A conversation with a co-packer who also makes a competing product is exactly the moment an NDA earns its keep. Signing one costs nothing; not having one means the only protection you have is that the other side chooses to behave.",
    negotiateCarefully: [
      {
        point: "Non-use, not just non-disclosure",
        detail:
          "An NDA that only stops them telling other people does not stop them using what you showed them. If the risk is that they build it themselves, you need a use restriction, not just a secrecy restriction.",
      },
      {
        point: "How long the obligation lasts",
        detail:
          "A two-year term is common in general commercial NDAs, but a recipe does not stop being valuable in two years. Consider a longer or indefinite tail for anything you treat as a trade secret.",
      },
      {
        point: "What counts as confidential",
        detail:
          "A requirement that everything be marked CONFIDENTIAL in writing sounds tidy and fails in practice, because most of what you disclose happens verbally in a plant tour or a call.",
      },
      {
        point: "Residuals clauses",
        detail:
          "A residuals clause lets the other side use anything their people remember without notes. In a formulation context that can swallow the entire agreement.",
      },
      {
        point: "Return or destruction of materials",
        detail:
          "Say what happens to your specifications, samples and files when the conversation ends, and by when.",
      },
    ],
    redFlags: [
      "One-way obligations in a document titled 'mutual'.",
      "A residuals clause permitting use of unaided memory.",
      "A confidentiality period that expires while your product is still on shelf.",
      "No obligation to return or destroy materials.",
      "A definition of Confidential Information that only covers written, marked disclosures.",
    ],
    timeToComplete: "10 minutes to fill · same-day to sign",
  },

  placeholders: [
    { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
    { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
    { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
    { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
    { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
    { token: "[COUNTERPARTY NAME]", label: "Other party's legal name", example: "Midwest Co-Packing, Inc." },
    { token: "[COUNTERPARTY STATE]", label: "Other party's state of formation", example: "Ohio" },
    { token: "[COUNTERPARTY ENTITY TYPE]", label: "Other party's entity type", example: "corporation" },
    { token: "[COUNTERPARTY ADDRESS]", label: "Other party's address", example: "500 Industrial Way, Columbus, OH 43215" },
    { token: "[PURPOSE]", label: "Purpose of the discussions", example: "evaluating a potential co-manufacturing relationship" },
    { token: "[TERM YEARS]", label: "Years the agreement runs", example: "3" },
    { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "5" },
    { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
    { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
  ],

  body: `This Mutual Non-Disclosure Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS]; and

  [COUNTERPARTY NAME], a [COUNTERPARTY STATE] [COUNTERPARTY ENTITY TYPE], with a principal
  place of business at [COUNTERPARTY ADDRESS].

Each party may disclose Confidential Information to the other. Each party is referred to as
the "Disclosing Party" when disclosing and the "Receiving Party" when receiving.

1. PURPOSE
The parties wish to explore [PURPOSE] (the "Purpose"). In connection with the Purpose, each
party may disclose Confidential Information to the other.

2. CONFIDENTIAL INFORMATION
"Confidential Information" means any non-public information disclosed by the Disclosing Party
to the Receiving Party, in any form, whether or not marked or identified as confidential,
including without limitation: formulas, recipes, formulations and specifications; ingredient
identities, sources and costs; supplier, co-manufacturer and distributor identities and terms;
pricing, margins, forecasts and financial information; product development plans, test results
and shelf-life data; customer and retailer lists and pipeline information; packaging designs
and artwork not yet released; and business plans and strategies.

Confidential Information does not include information that the Receiving Party can demonstrate:
(a) was already known to it without a duty of confidentiality before disclosure; (b) is or
becomes publicly available through no act or omission of the Receiving Party; (c) is rightfully
received from a third party without a duty of confidentiality; or (d) is independently developed
by the Receiving Party without use of or reference to the Disclosing Party's Confidential
Information.

3. OBLIGATIONS OF THE RECEIVING PARTY
The Receiving Party shall:
(a) hold the Confidential Information in confidence and protect it using at least the degree of
    care it uses for its own confidential information, and in no event less than reasonable care;
(b) not disclose the Confidential Information to any third party without the Disclosing Party's
    prior written consent; and
(c) not use the Confidential Information for any purpose other than the Purpose. Without
    limiting the foregoing, the Receiving Party shall not use the Confidential Information to
    develop, manufacture, formulate, market or sell any product for itself or for any third party.

4. PERMITTED DISCLOSURES
The Receiving Party may disclose Confidential Information to its employees, officers, directors,
and professional advisors who have a need to know it for the Purpose and who are bound by
confidentiality obligations at least as protective as those in this Agreement. The Receiving
Party remains responsible for any breach by those persons.

If the Receiving Party is required by law or legal process to disclose Confidential Information,
it shall, to the extent legally permitted, give the Disclosing Party prompt written notice so the
Disclosing Party may seek a protective order, and shall disclose only that portion legally
required.

5. NO LICENSE; NO OWNERSHIP TRANSFER
All Confidential Information remains the property of the Disclosing Party. Nothing in this
Agreement grants the Receiving Party any license or right in or to any Confidential Information,
or any patent, trademark, copyright, trade secret or other intellectual property right of the
Disclosing Party, whether by implication, estoppel or otherwise.

6. NO OBLIGATION TO PROCEED
Nothing in this Agreement obligates either party to proceed with any transaction, to disclose any
particular information, or to enter into any further agreement.

7. RETURN OR DESTRUCTION
Upon the Disclosing Party's written request, or upon termination of this Agreement, the Receiving
Party shall promptly return or destroy all Confidential Information in its possession, including
all copies, samples, prototypes and derivative materials, and shall confirm in writing that it has
done so. The Receiving Party may retain one copy solely for legal or compliance archival purposes,
which copy remains subject to this Agreement.

8. TERM AND SURVIVAL
This Agreement begins on the Effective Date and continues for [TERM YEARS] years, unless earlier
terminated by either party on written notice. The obligations in Sections 2 through 5 survive for
[CONFIDENTIALITY YEARS] years after the date of disclosure. Obligations with respect to any
information that constitutes a trade secret continue for as long as that information remains a
trade secret.

9. NO WARRANTY
All Confidential Information is provided "AS IS." Neither party makes any representation or
warranty as to its accuracy or completeness.

10. REMEDIES
Each party acknowledges that a breach of this Agreement may cause harm for which monetary damages
alone may be an inadequate remedy, and that the non-breaching party may seek equitable relief in
addition to any other remedies available to it.

11. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to
its conflict of laws principles. The parties consent to venue in [VENUE].

12. GENERAL
This Agreement is the entire agreement between the parties regarding its subject matter and
supersedes all prior discussions. It may be amended only in a writing signed by both parties.
Neither party may assign this Agreement without the other's prior written consent, except to a
successor in connection with a merger or sale of substantially all assets. If any provision is
held unenforceable, the remainder continues in effect. This Agreement may be signed in
counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [COUNTERPARTY NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
},

  {
    id: "one-way-nda",
    docId: "one-way-nda",
    title: "ONE-WAY NON-DISCLOSURE AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Only you are handing over something worth protecting — a formula to a lab, a brand concept to a designer, a cost model to a broker, sales data to a candidate. Nothing they show you needs protecting in return.",
      whyItMatters:
        "Most early CPG disclosure runs one direction, and reaching for a mutual form out of habit quietly costs you twice. It hands the other side confidentiality protection they never needed, and it gives them a claim that whatever they showed you now limits what you can do next. A designer who becomes a confidential discloser can later argue your packaging direction came out of their protected material. Match the paper to the actual flow of information.",
      negotiateCarefully: [
        {
          point: "Keep the asymmetry deliberate",
          detail:
            "If the other side asks to make it mutual, ask what they intend to disclose. Often the honest answer is nothing, and the request is a form habit rather than a need. If they genuinely will disclose, switch to the mutual form rather than bolting reciprocal obligations onto this one.",
        },
        {
          point: "Non-use as well as non-disclosure",
          detail:
            "Stopping them from telling other people does not stop them from using what they saw. Tie a non-use covenant to a narrowly written purpose, so 'evaluating a business relationship' cannot be read to include building the product.",
        },
        {
          point: "Samples are a disclosure",
          detail:
            "A finished bottle or bar sent for a quote can be sent straight to an analytical lab. Cover physical samples and prototypes in the definition, and add an express no-analysis, no-deconstruction covenant.",
        },
        {
          point: "Term versus survival",
          detail:
            "The period during which you may disclose and the period the obligation lasts are two different clocks. Confusing them is the most common drafting error in these forms, and it ends the obligation when the project ends.",
        },
        {
          point: "Who else gets to see it",
          detail:
            "'Affiliates and advisors' with no obligation to bind them individually can put your formula inside a parent company that also owns a competing brand. Name the categories, require written flow-down, and keep the recipient responsible for the people it tells.",
        },
      ],
      redFlags: [
        "A term that ends the confidentiality obligation when the project ends.",
        "Any clause converting disclosure into a licence to use or improve what was disclosed.",
        "Refusal to accept a no-reverse-engineering covenant while still asking you for samples.",
        "Permitted recipients written broadly enough to include unnamed affiliates.",
        "A receiving party who works with directly competing brands and will not separate personnel or files.",
      ],
      timeToComplete: "10 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[RECIPIENT NAME]", label: "Receiving party's legal name", example: "Harbor Flavor Labs, Inc." },
      { token: "[RECIPIENT ADDRESS]", label: "Receiving party's address", example: "44 Water St, Providence, RI 02903" },
      { token: "[PURPOSE]", label: "The single purpose they may use it for", example: "quoting and, if engaged, producing a shelf-stable canned beverage for the Company" },
      { token: "[TERM YEARS]", label: "Years you may keep disclosing under this agreement", example: "2" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives for ordinary business information", example: "5" },
      { token: "[RETURN DAYS]", label: "Days to return or destroy after a request", example: "15" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This One-Way Non-Disclosure Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS] (the "Disclosing Party"); and

  [RECIPIENT NAME], with an address at [RECIPIENT ADDRESS] (the "Receiving Party").

1. DIRECTION OF DISCLOSURE
This Agreement is one-way and is intended to be. Only the Disclosing Party discloses Confidential
Information under it, and only the Receiving Party takes on obligations under it. Nothing the
Receiving Party says, sends, shows or demonstrates to the Disclosing Party is Confidential
Information under this Agreement, and the Disclosing Party assumes no confidentiality, non-use or
non-competition obligation of any kind by signing it. If the Receiving Party intends to disclose
its own confidential information, the parties will sign a separate mutual agreement covering it
rather than relying on this one.

2. PURPOSE
The Receiving Party may use the Confidential Information solely for the following purpose:
[PURPOSE] (the "Purpose"). Any other use is outside this Agreement.

3. CONFIDENTIAL INFORMATION
"Confidential Information" means any non-public information the Disclosing Party makes available to
the Receiving Party, in any form and whether or not marked or identified as confidential, including
without limitation: formulas, formulations, recipes, batch sheets, usage rates and process
parameters; specifications, shelf-life, stability, sensory and analytical data; ingredient
identities, grades, sources and costs; supplier, co-manufacturer, broker and distributor identities
and terms; pricing, margins, landed cost, forecasts and financial information; product development
plans and timelines; customer, retailer and pipeline information; unreleased packaging, artwork,
brand names and positioning; and business plans and strategies.

Confidential Information includes information disclosed orally or visually, including on a plant
tour, a bench session, a video call or a tasting, whether or not it is later confirmed in writing.
It also includes physical materials: samples, retains, prototypes, pilot lots, ingredient
deliveries and packaging mock-ups.

Confidential Information does not include information the Receiving Party can demonstrate by
written records: (a) was already known to it without a duty of confidentiality before disclosure;
(b) is or becomes publicly available through no act or omission of the Receiving Party;
(c) is rightfully received from a third party without a duty of confidentiality; or
(d) is independently developed by the Receiving Party without use of or reference to the
Confidential Information.

4. OBLIGATIONS OF THE RECEIVING PARTY
The Receiving Party shall:
(a) hold the Confidential Information in confidence and protect it using at least the degree of
    care it uses for its own confidential information, and in no event less than reasonable care;
(b) not disclose the Confidential Information to any person except as Section 5 permits;
(c) not use the Confidential Information for any purpose other than the Purpose. Without limiting
    the foregoing, the Receiving Party shall not use the Confidential Information to develop,
    formulate, manufacture, market or sell any product for itself or for any other party; and
(d) not use information retained in the unaided memory of its personnel as a way around
    subsections (b) and (c). There is no residuals exception to this Agreement.

5. PERMITTED RECIPIENTS
The Receiving Party may disclose Confidential Information only to those of its employees, officers,
directors and professional advisors who need it for the Purpose and who are bound by written
confidentiality obligations at least as protective as those in this Agreement. Disclosure to any
affiliate, subcontractor, laboratory or outside facility requires the Disclosing Party's prior
written consent, and the Receiving Party shall bind that party in writing before disclosing. The
Receiving Party remains responsible for any breach by any person to whom it discloses.

6. NO ANALYSIS OR REVERSE ENGINEERING
The Receiving Party shall not analyse, deconstruct, reverse engineer or have tested any sample,
retain, prototype or finished product provided by the Disclosing Party, and shall not attempt to
determine its composition, formulation or process, except to the extent expressly necessary for the
Purpose and expressly authorised in writing in advance. Any result derived from such an authorised
analysis is Confidential Information of the Disclosing Party.

7. NO LICENCE; NO OWNERSHIP TRANSFER
All Confidential Information remains the property of the Disclosing Party. Nothing in this
Agreement, and nothing done in the course of disclosure, grants the Receiving Party any licence or
right in or to any Confidential Information or any patent, trademark, copyright, trade secret or
other intellectual property right of the Disclosing Party, whether by implication, estoppel or
otherwise. Being shown something is not permission to work on it.

8. COMPELLED DISCLOSURE
If the Receiving Party is required by law or legal process to disclose Confidential Information, it
shall, to the extent legally permitted, give the Disclosing Party prompt written notice before
disclosing so the Disclosing Party may seek protective treatment, shall cooperate with the
Disclosing Party at the Disclosing Party's expense, and shall disclose only that portion legally
required.

9. RETURN OR DESTRUCTION
Upon the Disclosing Party's written request, or upon expiry or termination of this Agreement, the
Receiving Party shall within [RETURN DAYS] days return or destroy all Confidential Information in
its possession or control, including all copies, notes, analyses, samples, retains, prototypes and
derivative materials, and shall confirm in writing signed by an authorised officer that it has done
so. The Receiving Party may retain one copy solely for legal or compliance archival purposes and
copies held in routine, non-targeted electronic backups, all of which remain subject to this
Agreement for as long as they are retained.

10. TERM AND SURVIVAL
This Agreement begins on the Effective Date and continues for [TERM YEARS] years, during which the
Disclosing Party may make disclosures under it. Either party may terminate the disclosure period on
written notice; termination does not end the Receiving Party's obligations. The obligations in
Sections 3 through 8 survive for [CONFIDENTIALITY YEARS] years after the date of each disclosure,
and obligations with respect to any Confidential Information that constitutes a trade secret,
including any formula or formulation, continue for as long as that information remains a trade
secret.

11. NO OBLIGATION TO PROCEED
Nothing in this Agreement obligates either party to proceed with any transaction, to disclose any
particular information, or to enter into any further agreement.

12. NO WARRANTY
All Confidential Information is provided "AS IS." The Disclosing Party makes no representation or
warranty as to its accuracy or completeness.

13. REMEDIES
The Receiving Party acknowledges that a breach of this Agreement may cause harm for which monetary
damages alone may be an inadequate remedy, and that the Disclosing Party may seek equitable relief
in addition to any other remedies available to it.

14. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

15. GENERAL
This Agreement is the entire agreement between the parties regarding its subject matter and
supersedes all prior discussions. It may be amended only in a writing signed by both parties. The
Receiving Party may not assign this Agreement without the Disclosing Party's prior written consent,
including in connection with a merger or a sale of substantially all of its assets. If any provision
is held unenforceable, the remainder continues in effect. This Agreement may be signed in
counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [RECIPIENT NAME]
(Disclosing Party)                        (Receiving Party)


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  {
    id: "contractor-nda",
    docId: "contractor-nda",
    title: "CONTRACTOR CONFIDENTIALITY AND WORK PRODUCT AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before a freelance designer, formulator, photographer, copywriter or fractional operator sees anything about the product — usually before the first working call, not when the invoice arrives.",
      whyItMatters:
        "A plain contractor NDA stops the freelancer from showing your label to anyone else. It does not make the label yours. Absent a written assignment, the person who created the artwork, the photography or the formulation generally holds the rights to it, and paying the invoice does not move them. Founders find this out during a trademark filing, a packaging reprint or an acquirer's IP diligence — which is why this template does both jobs in one signature: confidentiality and a present assignment of what the contractor makes for you.",
      negotiateCarefully: [
        {
          point: "Ownership has to be present tense",
          detail:
            "'Contractor will assign' leaves you holding a promise instead of the asset, and it depends on the contractor still taking your calls in two years. The operative words are that the contractor assigns now, with a further-assurances obligation as backup.",
        },
        {
          point: "The prior inventions schedule",
          detail:
            "A blank schedule is an open invitation to claim later that the core of the work was pre-existing and never transferred. Fill it in specifically, or have the contractor write 'None' and sign under it.",
        },
        {
          point: "Working files, not final exports",
          detail:
            "'Final files' usually means flattened PDFs and JPEGs. Name the layered artwork, the dielines, the fonts used, the raw camera files, the project files and the bench notes, and make delivery of them a condition of the last payment.",
        },
        {
          point: "Non-use across their other clients",
          detail:
            "A formulator or designer with three brands in your category can legitimately reuse an approach unless the agreement restricts use, not just disclosure. Ask for a defined restriction tied to your category rather than a general non-compete.",
        },
        {
          point: "Portfolio rights and the launch date",
          detail:
            "Unrestricted portfolio rights put your unreleased packaging on a public site before the product ships. Push portfolio use past the public launch date and exclude anything showing formulations, costs or supplier names.",
        },
      ],
      redFlags: [
        "A contractor who will not sign any assignment and offers a licence to their deliverables instead, without saying so plainly.",
        "Portfolio or self-promotion rights that start immediately and cover unreleased work.",
        "No obligation to return or delete working files and bench notes.",
        "Confidentiality that binds the individual but not the studio or agency that will actually do the work.",
        "A subcontracted illustrator, retoucher or lab tech who is never named and never bound.",
      ],
      timeToComplete: "20 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[CONTRACTOR NAME]", label: "Contractor's legal name (individual or studio entity)", example: "Rill Design Studio LLC" },
      { token: "[CONTRACTOR ADDRESS]", label: "Contractor's address", example: "18 Bell Ave, Portland, OR 97209" },
      { token: "[SERVICES]", label: "What the contractor is doing", example: "primary packaging design for the Company's canned tea line" },
      { token: "[DELIVERABLES]", label: "Deliverables, including file types", example: "logo suite, front and back label artwork, dieline, layered source files, packaged fonts" },
      { token: "[PRODUCT CATEGORY]", label: "Your product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[CATEGORY RESTRICTION MONTHS]", label: "Months the non-use restriction runs in your category", example: "12" },
      { token: "[PUBLIC LAUNCH DATE]", label: "Date portfolio use may begin", example: "the public launch date of the Product" },
      { token: "[RETURN DAYS]", label: "Days to return or delete materials", example: "15" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives for ordinary business information", example: "5" },
      { token: "[PRIOR MATERIALS]", label: "Contractor's pre-existing material (Schedule A), or 'None'", example: "None" },
      { token: "[THIRD PARTY MATERIALS]", label: "Third-party fonts, stock or assets in the deliverables (Schedule B)", example: "Font: Sabon (desktop + packaging licence, held by Contractor)" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Contractor Confidentiality and Work Product Agreement (this "Agreement") is entered into as
of [EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS] (the "Company"); and

  [CONTRACTOR NAME], with an address at [CONTRACTOR ADDRESS] (the "Contractor").

1. THE ENGAGEMENT
The Contractor is performing the following services for the Company: [SERVICES] (the "Services"),
producing the following deliverables: [DELIVERABLES] (the "Deliverables"). This Agreement governs
confidentiality and ownership. Fees, scope, schedule and other commercial terms are set out in the
parties' separate services agreement, statement of work or purchase order, and this Agreement
applies to all Services the Contractor performs for the Company whether or not a written statement
of work is in place at the time.

2. CONFIDENTIAL INFORMATION
"Confidential Information" means any non-public information of the Company made available to the
Contractor, in any form and whether or not marked confidential, including without limitation:
formulas, formulations, bench notes and specifications; ingredient identities, grades, sources and
costs; supplier, co-manufacturer, broker and distributor identities and terms; pricing, margins,
landed cost, forecasts and financial information; unreleased artwork, packaging, brand names,
claims and positioning; launch timing and retailer plans; and customer and pipeline information.
It includes information disclosed orally or visually, and physical materials including samples,
prototypes, retains and packaging mock-ups.

Confidential Information does not include information the Contractor can demonstrate by written
records was already known to it without a duty of confidentiality, is or becomes public through no
act or omission of the Contractor, is rightfully received from a third party without a duty of
confidentiality, or was independently developed without use of or reference to the Confidential
Information.

3. CONFIDENTIALITY AND NON-USE
The Contractor shall hold the Confidential Information in confidence, shall not disclose it to any
person except as Section 4 permits, and shall use it only to perform the Services. The Contractor
shall not use the Confidential Information for any other client or for the Contractor's own
account. There is no residuals exception to this Agreement: information retained in the unaided
memory of the Contractor or its personnel remains subject to this Section.

For [CATEGORY RESTRICTION MONTHS] months after the Services end, the Contractor shall not use any
of the Company's Confidential Information in work for any other party in the [PRODUCT CATEGORY]
category. This restricts the use of the Company's information; it is not a general restriction on
the Contractor's right to work in the category.

4. PERSONNEL AND SUBCONTRACTORS
The Contractor may disclose Confidential Information only to its own personnel who need it to
perform the Services. The Contractor shall not engage any subcontractor, studio, retoucher,
illustrator, laboratory or other third party to perform any part of the Services without the
Company's prior written consent. Before any approved subcontractor begins work or receives
Confidential Information, the Contractor shall bind that subcontractor in writing to
confidentiality, non-use and work product assignment obligations at least as protective as those in
this Agreement, and shall assign or otherwise secure for the Company all rights that subcontractor
creates. The Contractor remains responsible for every act and omission of its personnel and
subcontractors. On request, the Contractor shall identify in writing every person who has worked on
the Services.

5. WORK PRODUCT
"Work Product" means everything the Contractor conceives, creates, develops, writes, designs,
photographs, films, records, formulates or reduces to practice, alone or with others, in the course
of or arising from the Services, including without limitation the Deliverables and all: designs,
logos, marks, artwork, illustrations, dielines and structural packaging drawings; photography,
video, audio and raw camera files; copy, scripts, claims language and technical documentation;
formulas, formulations, prototypes, bench notes, trial records and specifications; software, code
and website materials; and all drafts, concepts, rejected routes and working files created along
the way.

6. ASSIGNMENT OF WORK PRODUCT
The Contractor hereby irrevocably assigns to the Company all right, title and interest worldwide in
and to the Work Product, including all copyrights, trademark rights, patent rights, trade secret
rights and other intellectual property rights in it, together with the right to sue for and recover
damages for any past, present or future infringement or misappropriation. This is a present
assignment, effective as each item of Work Product is created, and is not a promise to assign in
the future.

To the extent any right in the Work Product cannot be assigned as a matter of law, the Contractor
grants the Company an exclusive, worldwide, perpetual, irrevocable, fully paid, sublicensable and
transferable licence to use, reproduce, modify, distribute, publicly display and otherwise exploit
that right for any purpose. To the extent permitted, the Contractor waives, and agrees not to
assert against the Company or its licensees, any moral rights or rights of attribution or integrity
in the Work Product.

The Contractor shall, at the Company's request and expense, sign any further documents and take any
further actions the Company reasonably requires to obtain, perfect, record or enforce its rights in
the Work Product, including assignments in recordable form, and this obligation continues after the
Services end.

7. PRIOR AND BACKGROUND MATERIAL
Schedule A lists all material the Contractor owned or controlled before the Effective Date, or
developed entirely outside the Services, that the Contractor intends to incorporate into the Work
Product ("Prior Material"). Anything not listed on Schedule A is not Prior Material and is covered
by Section 6. If the Contractor incorporates Prior Material into any Deliverable, the Contractor
grants the Company a non-exclusive, worldwide, perpetual, irrevocable, fully paid, sublicensable and
transferable licence to use, reproduce, modify and distribute that Prior Material as part of the
Deliverable and any derivative of it, including on packaging and in paid media.

8. THIRD-PARTY MATERIAL
Schedule B lists every third-party font, stock image, illustration, music track, plug-in or other
licensed asset embedded in or required to use the Deliverables, together with the licence that
covers it and who holds that licence. The Contractor represents that each such licence permits the
Company's intended use, including reproduction on packaging and use in paid advertising. The
Contractor shall not incorporate any third-party material not listed on Schedule B without the
Company's prior written consent.

9. DELIVERY OF FILES AND MATERIALS
On completion of the Services, on termination, and at any time on the Company's request, the
Contractor shall deliver to the Company all Work Product in its native, editable form, including
layered and working design files, dielines and structural files, packaged or listed fonts, raw and
original camera files, editing project files, 3D and render files, code repositories, bench notes,
trial records and specifications, together with the exports. Delivery of final flattened files
alone does not satisfy this Section.

10. RETURN AND DELETION
Within [RETURN DAYS] days after the Services end or after the Company's written request, the
Contractor shall return or delete all Confidential Information and all copies of the Work Product in
its possession or control, including files held in email, personal devices, cloud storage and
shared drives, and all samples, prototypes and retains, and shall confirm in writing that it has
done so. The Contractor may retain copies held in routine, non-targeted electronic backups and one
copy required for the Contractor's own legal or accounting records, which remain subject to this
Agreement.

11. PORTFOLIO USE
The Contractor may display finished Deliverables in its portfolio and self-promotional materials
only on and after [PUBLIC LAUNCH DATE], and only in a form that does not disclose any formulation,
ingredient, cost, supplier, sales or forecast information. The Contractor shall remove any such
display on the Company's written request. This is a limited permission, not a licence to use the
Deliverables commercially or for any other client.

12. WARRANTIES
The Contractor represents and warrants that: it has the right to enter into this Agreement and is
not bound by any obligation that conflicts with it; the Work Product is original to the Contractor
except for Prior Material and the third-party material listed on Schedule B; and to the best of the
Contractor's knowledge the Work Product does not infringe the rights of any third party.

13. INDEPENDENT CONTRACTOR
The Contractor is an independent contractor. Nothing in this Agreement creates an employment,
partnership, joint venture or agency relationship, and this Agreement does not obligate the Company
to engage the Contractor for any minimum amount of work.

14. TERM, SURVIVAL AND REMEDIES
This Agreement begins on the Effective Date and continues while the Contractor performs Services and
thereafter as set out here. The confidentiality and non-use obligations survive for
[CONFIDENTIALITY YEARS] years after the Services end, and continue for as long as the information
remains a trade secret with respect to any formula, formulation or other trade secret material. The
assignment in Section 6 and the obligations in Sections 7 through 12 survive indefinitely. Each
party acknowledges that a breach may cause harm for which monetary damages alone may be an
inadequate remedy, and that the non-breaching party may seek equitable relief in addition to any
other remedy available to it.

15. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

16. GENERAL
This Agreement is the entire agreement between the parties regarding confidentiality and ownership
of the Work Product and supersedes all prior discussions on those subjects. Where it conflicts with
a services agreement, statement of work or purchase order, this Agreement controls on
confidentiality and ownership. It may be amended only in a writing signed by both parties. The
Contractor may not assign this Agreement without the Company's prior written consent; the Company
may assign it to a successor in connection with a merger or a sale of substantially all of its
assets. If any provision is held unenforceable, the remainder continues in effect. This Agreement
may be signed in counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CONTRACTOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — PRIOR MATERIAL
─────────────────────────────────────────────────────────────────────────────
List every item the Contractor owned or controlled before the Effective Date and intends to use in
the Work Product. Write "None" if there is none. An unlisted item is not Prior Material.

[PRIOR MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Contractor initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — THIRD-PARTY MATERIAL AND LICENCES
─────────────────────────────────────────────────────────────────────────────
List every third-party font, image, illustration, music track or other licensed asset in or needed
for the Deliverables, the licence that covers it, and who holds that licence.

[THIRD PARTY MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Contractor initials: __________          Company initials: __________
`,
  },

  {
    id: "manufacturing-nda",
    docId: "manufacturing-nda",
    title: "MANUFACTURING CONFIDENTIALITY AND NON-USE AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before you send a formula, specification, batch sheet or sample to any plant — including the ones that are only quoting, and the ones a friend introduced you to.",
      whyItMatters:
        "Quoting a co-packing job requires the formula. That means the sourcing process itself puts your product in front of every plant you talk to, most of which also run products for your competitors on the same lines. A clause that only stops disclosure lets a plant build a near-identical product for another customer without ever mentioning your name. For a young CPG brand the formula and the supplier list are usually the only genuinely defensible things it owns, and this is the document that decides what a plant may do with them.",
      negotiateCarefully: [
        {
          point: "Non-use, written to reach their other customers",
          detail:
            "The clause that matters is not 'you will not tell anyone' but 'you will not use this, for yourself or for anyone else, to make a similar product'. Say it in those words. Without it, the plant can produce a close cousin of your product for a competitor and never breach a secrecy obligation.",
        },
        {
          point: "Residuals",
          detail:
            "Language permitting use of anything an employee remembers without notes swallows the whole agreement in a formulation context — the useful part of a formula is exactly what a process engineer remembers. Strike it, or the rest of the document is decoration.",
        },
        {
          point: "Samples and retained lots",
          detail:
            "Documents get returned; sample retains stay in a freezer, and one bottle tells an analytical lab more than a specification sheet does. Cover samples, pilot lots and retains explicitly, prohibit analysis, and require destruction with certification.",
        },
        {
          point: "Their pre-existing IP carve-out",
          detail:
            "Plants reasonably want to protect their platform technology and base formulations. A carve-out drafted broadly enough to cover 'process improvements arising from work with customers' can be read to cover the product you brought them. Narrow it to what they can evidence they had before you arrived.",
        },
        {
          point: "How long it lasts",
          detail:
            "A fixed expiry means your formula becomes freely usable on a date certain, which is not how a trade secret works. Ask for a defined term on ordinary business information and protection for trade secret material lasting as long as it stays secret.",
        },
      ],
      redFlags: [
        "Disclosure-only obligations with no restriction on use.",
        "A residuals clause permitting use of unaided memory.",
        "A carve-out for the manufacturer's own formulations broad enough to be read to cover yours.",
        "Confidentiality expiring on a short fixed date with no trade secret carve-out.",
        "Nothing covering samples, retains or physical product.",
        "Silence on subcontracted facilities and co-manufacturers the plant may use.",
      ],
      timeToComplete: "25 minutes to fill · a few days if the plant's legal team reviews it",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[MANUFACTURER NAME]", label: "Manufacturer's legal name", example: "Midwest Co-Packing, Inc." },
      { token: "[MANUFACTURER STATE]", label: "Manufacturer's state of formation", example: "Ohio" },
      { token: "[MANUFACTURER ENTITY TYPE]", label: "Manufacturer's entity type", example: "corporation" },
      { token: "[MANUFACTURER ADDRESS]", label: "Manufacturer's address", example: "500 Industrial Way, Columbus, OH 43215" },
      { token: "[FACILITY LOCATION]", label: "Facility that will hold your information", example: "Columbus, OH plant" },
      { token: "[PRODUCT DESCRIPTION]", label: "The product being quoted or produced", example: "a shelf-stable 12 oz canned sparkling tea" },
      { token: "[PRODUCT CATEGORY]", label: "Your product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[PURPOSE]", label: "The single purpose the plant may use it for", example: "quoting and, if awarded, producing the Product for the Company" },
      { token: "[TERM YEARS]", label: "Years you may keep disclosing under this agreement", example: "3" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives for ordinary business information", example: "7" },
      { token: "[NON USE YEARS]", label: "Years the similar-product restriction runs after the last disclosure", example: "5" },
      { token: "[RETURN DAYS]", label: "Days to return or destroy after a request", example: "30" },
      { token: "[SAMPLE RETENTION DAYS]", label: "Days the plant may keep samples and retains", example: "90" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Manufacturing Confidentiality and Non-Use Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [MANUFACTURER NAME], a [MANUFACTURER STATE] [MANUFACTURER ENTITY TYPE], with a principal
  place of business at [MANUFACTURER ADDRESS] (the "Manufacturer").

1. PURPOSE
The Company is disclosing information to the Manufacturer so the Manufacturer may quote, develop,
trial and, if awarded, produce [PRODUCT DESCRIPTION] (the "Product"). The Manufacturer may use the
Company's Confidential Information solely for the following purpose: [PURPOSE] (the "Purpose").
Any other use is outside this Agreement.

This Agreement applies whether or not a supply, co-packing or manufacturing agreement follows, and
it covers information already disclosed in the parties' discussions before the Effective Date.

2. CONFIDENTIAL INFORMATION
"Confidential Information" means any non-public information of the Company made available to the
Manufacturer, in any form and whether or not marked confidential, including without limitation:

(a) Formula and process. Formulas, formulations, recipes, batch sheets, master batch records,
    usage rates, order of addition, mixing, heating, filling, carbonation, pasteurisation and
    other process parameters, hold times, yields, overages and scale-up notes.
(b) Specifications. Raw material, finished good, packaging and quality specifications, tolerances,
    shelf-life, stability, micro, sensory and analytical data, and testing protocols and results.
(c) Sourcing. The identity of every ingredient, flavour, extract, protein, sweetener, colour,
    packaging and label supplier, the specific grade, spec number and item code used, approved
    substitutions, and prices, minimums and lead times.
(d) Commercial information. Costs, tolling rates, margins, landed cost, volumes, forecasts,
    production schedules, launch timing, retailer and distributor commitments and pipeline.
(e) Brand material. Unreleased artwork, packaging structures, brand names, claims and positioning.
(f) Physical material. Samples, retains, benchtop and pilot lots, prototypes, ingredient
    deliveries, packaging mock-ups and any product produced using the Confidential Information.

Confidential Information includes anything disclosed orally or visually, including on a plant walk,
a trial run, a tasting or a call, whether or not confirmed in writing afterwards. It also includes
the fact and content of the parties' discussions and the fact that the Company is evaluating the
Manufacturer.

Confidential Information does not include information the Manufacturer can demonstrate by
contemporaneous written records: (a) was already known to it without a duty of confidentiality
before disclosure; (b) is or becomes publicly available through no act or omission of the
Manufacturer; (c) is rightfully received from a third party without a duty of confidentiality; or
(d) is independently developed by the Manufacturer by personnel who had no access to the
Confidential Information.

3. CONFIDENTIALITY
The Manufacturer shall hold the Confidential Information in confidence, protect it using at least
the degree of care it uses for its own confidential information and in no event less than
reasonable care, and not disclose it to any person except as Section 5 permits.

4. NON-USE, INCLUDING NO SIMILAR PRODUCT FOR OTHERS
This Section is the operative restriction and is separate from Section 3.

The Manufacturer shall not use the Confidential Information for any purpose other than the Purpose.
Without limiting that, the Manufacturer shall not use, refer to or rely on any Confidential
Information, in whole or in part, to develop, formulate, reformulate, trial, scale, produce,
package, market or sell any product for itself, for any of its other customers, or for any other
party. This restriction expressly includes producing a product that is similar to the Product, or
that is derived from or approximates the Company's formula, process parameters, specification or
sourcing choices, for any other party.

There is no residuals exception to this Agreement. The Manufacturer shall not use Confidential
Information retained in the unaided memory of its personnel as a way around this Section, and shall
not permit personnel who have had access to the Confidential Information to apply it to another
customer's product in the [PRODUCT CATEGORY] category.

The Manufacturer shall not file any patent application, or claim any invention, that discloses or
relies on the Confidential Information.

This Section applies during the term of this Agreement and for [NON USE YEARS] years after the last
disclosure, and continues for as long as the information remains a trade secret with respect to any
formula, formulation, process parameter or other trade secret material.

5. WHO MAY SEE IT
The Manufacturer may disclose Confidential Information only to those of its employees, officers and
professional advisors at [FACILITY LOCATION] who need it for the Purpose and who are bound by
written confidentiality and non-use obligations at least as protective as those in this Agreement.

Disclosure to any affiliate, parent, sister facility, broker, laboratory, ingredient supplier or
subcontracted co-manufacturer requires the Company's prior written consent. Where the Manufacturer
subcontracts any part of a run, blending step, filling step or laboratory test, the obligations of
this Agreement follow the Confidential Information to that party, which must be bound in writing
before it receives anything. The Manufacturer remains responsible for every act and omission of any
person to whom it discloses. On the Company's request, the Manufacturer shall provide a written list
of the individuals and facilities that have had access.

6. NO ANALYSIS OR REVERSE ENGINEERING OF SAMPLES
The Manufacturer shall not analyse, deconstruct, reverse engineer, chemically or analytically
characterise, or have tested by any third party, any sample, retain, pilot lot, prototype or
finished unit of the Product, and shall not attempt to determine its composition, formulation or
process, except to the extent expressly necessary for the Purpose and expressly authorised by the
Company in writing in advance. Any result, data set or report derived from an authorised analysis is
Confidential Information of the Company and is Company property.

The Manufacturer may retain samples and retains only for the period required for the Purpose and in
any event no longer than [SAMPLE RETENTION DAYS] days after the Purpose ends, after which it shall
destroy them and certify the destruction in writing. The Manufacturer shall not send any sample,
retain or unit of the Product to any third party without the Company's prior written consent.

7. HANDLING INSIDE THE MANUFACTURER'S SYSTEMS
The Manufacturer shall enter the Confidential Information into its recipe management, formulation,
ERP, quality, purchasing and laboratory systems only to the extent required for the Purpose, and
shall:
(a) store it in a record identified as belonging to the Company, and not as a house, platform,
    base or standard formulation of the Manufacturer;
(b) restrict access to named individuals who need it for the Purpose, and remove access when an
    individual no longer needs it or leaves the Manufacturer's employment;
(c) not copy, adapt, template or reuse the record, or any part of it, in the formulation,
    specification or costing record of any other customer or of the Manufacturer itself;
(d) not make the record, or any ingredient combination, usage rate or process parameter drawn from
    it, discoverable in a search, report, dashboard or costing tool used for other customers'
    projects;
(e) not export, transfer or replicate the record to any other facility, affiliate or system without
    the Company's prior written consent; and
(f) on the Company's written request, delete or irreversibly segregate the record and confirm in
    writing that it has done so, subject only to Section 9.

Entering the Confidential Information into a system does not change who owns it. A record created
in the Manufacturer's systems from the Company's Confidential Information is Confidential
Information and remains the Company's property.

8. THE MANUFACTURER'S OWN INTELLECTUAL PROPERTY
Nothing in this Agreement transfers to the Company any equipment, tooling, general manufacturing
know-how, platform technology or base formulation that the Manufacturer owned or controlled before
the Effective Date and can evidence by contemporaneous written records, or that the Manufacturer
develops entirely independently of the Confidential Information (the "Manufacturer Background").
The Manufacturer Background does not include, and this Section does not permit the Manufacturer to
claim as its own, any formulation, specification, process parameter or improvement that is derived
from, incorporates or was developed using the Confidential Information.

9. RETURN OR DESTRUCTION
Upon the Company's written request, or upon expiry or termination of this Agreement, the
Manufacturer shall within [RETURN DAYS] days return or destroy all Confidential Information in its
possession or control, including all copies, notes, batch records, specification files, analytical
results, samples, retains, pilot lots and derivative materials, and shall confirm the return or
destruction in writing signed by an authorised officer. The Manufacturer may retain copies held in
routine, non-targeted electronic backups and one copy required for its own food safety, quality or
regulatory recordkeeping, all of which remain subject to this Agreement for as long as they are
retained and may not be used for any purpose other than that recordkeeping.

10. NO LICENCE; NO OWNERSHIP TRANSFER
All Confidential Information remains the property of the Company. Nothing in this Agreement grants
the Manufacturer any licence or right in or to any Confidential Information or any patent,
trademark, copyright, trade secret or other intellectual property right of the Company, whether by
implication, estoppel or otherwise.

11. COMPELLED DISCLOSURE
If the Manufacturer is required by law or legal process to disclose Confidential Information, or is
asked for it in a customer or certification audit, it shall, to the extent legally permitted, give
the Company prompt written notice before disclosing so the Company may seek protective treatment,
shall cooperate with the Company at the Company's expense, and shall disclose only that portion
required.

12. TERM AND SURVIVAL
This Agreement begins on the Effective Date and continues for [TERM YEARS] years, during which the
Company may make disclosures under it. Either party may terminate the disclosure period on written
notice; termination does not end the Manufacturer's obligations. The obligations in Sections 2
through 11 survive for [CONFIDENTIALITY YEARS] years after the date of each disclosure, and
obligations with respect to any Confidential Information that constitutes a trade secret, including
any formula, formulation, process parameter or specification, continue for as long as that
information remains a trade secret.

13. NO OBLIGATION TO PROCEED
Nothing in this Agreement obligates either party to proceed with any transaction, to disclose any
particular information, to place or accept any order, or to enter into any further agreement. Any
production relationship will be governed by a separate written agreement.

14. NO WARRANTY
All Confidential Information is provided "AS IS." The Company makes no representation or warranty as
to its accuracy or completeness.

15. REMEDIES
The Manufacturer acknowledges that the Confidential Information includes trade secrets, that a
breach of this Agreement may cause harm for which monetary damages alone may be an inadequate
remedy, and that the Company may seek equitable relief in addition to any other remedies available
to it.

16. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

17. GENERAL
This Agreement is the entire agreement between the parties regarding its subject matter and
supersedes all prior discussions. It may be amended only in a writing signed by both parties.
Neither party may assign this Agreement without the other's prior written consent, including in
connection with a merger or a sale of substantially all assets. If any provision is held
unenforceable, the remainder continues in effect. This Agreement may be signed in counterparts,
including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [MANUFACTURER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  {
    id: "product-development-nda",
    docId: "product-development-nda",
    title: "PRODUCT DEVELOPMENT AND CONFIDENTIALITY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "At the first substantive conversation with anyone who might help build the product — a formulator, a lab, a flavour house, a food scientist — before a brief, a benchmark sample or a target cost goes across.",
      whyItMatters:
        "Ownership of what gets invented is decided before the work begins, not after it works. The development conversation happens weeks before anyone drafts a services agreement, and by then a brief, a benchmark, a target price and two rounds of bench samples have already changed hands. A short combined document at the front of the relationship prevents the most expensive outcome in CPG development: a successful formula with a genuinely contested owner, negotiated at the moment your leverage is lowest.",
      negotiateCarefully: [
        {
          point: "Say who owns it in this document",
          detail:
            "Do not defer ownership to a services agreement that may never get signed. One clear paragraph — formulas, specifications and improvements developed for the brand belong to the brand — is worth more than a promise to sort it out later.",
        },
        {
          point: "Background material, identified in writing before the first trial",
          detail:
            "Every developer has base systems and platform know-how they genuinely own. Get them listed on a schedule up front. Left undefined, the developer can characterise the core of your solution as pre-existing after it works.",
        },
        {
          point: "Bench notes, trial records and samples",
          detail:
            "The reasoning is in the notes, not the final spec. A document that transfers only the winning formula leaves behind the eleven failed trials that explain why it wins, and a lab that keeps the retains keeps the version easiest to analyse.",
        },
        {
          point: "What happens if the project stops",
          detail:
            "Abandoned projects still produce usable formulations. Say explicitly that work product created before an early stop still belongs to you, and that confidentiality survives whether or not a project follows.",
        },
        {
          point: "Parallel work on the same brief",
          detail:
            "A developer running your project and a competitor's brief in the same month is the ordinary situation. Ask for a short exclusivity window on this specific brief rather than a general non-compete they will refuse.",
        },
      ],
      redFlags: [
        "A developer who will not discuss ownership until after the work is done.",
        "Silence on what happens to work product if the project is abandoned.",
        "Retention of all samples, retains and bench notes by the developer as a matter of policy.",
        "A confidentiality obligation that lapses if no project proceeds.",
        "A background-material schedule left blank, or described only as 'the developer's know-how'.",
      ],
      timeToComplete: "20 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[DEVELOPER NAME]", label: "Developer's legal name", example: "Harbor Flavor Labs, Inc." },
      { token: "[DEVELOPER ADDRESS]", label: "Developer's address", example: "44 Water St, Providence, RI 02903" },
      { token: "[PROJECT DESCRIPTION]", label: "The development project", example: "a shelf-stable sparkling tea with no added sugar at a target landed cost" },
      { token: "[PRODUCT CATEGORY]", label: "Your product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[PROJECT FEE]", label: "Fee or consideration for the work", example: "the fees set out in the parties' statement of work" },
      { token: "[EXCLUSIVITY MONTHS]", label: "Months the developer may not run the same brief for others", example: "6" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives for ordinary business information", example: "7" },
      { token: "[RETURN DAYS]", label: "Days to deliver or destroy materials after a request", example: "30" },
      { token: "[BACKGROUND MATERIALS]", label: "Developer's background material (Schedule A), or 'None'", example: "Developer's proprietary clouding emulsion base HFL-220" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Product Development and Confidentiality Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [DEVELOPER NAME], with an address at [DEVELOPER ADDRESS] (the "Developer").

1. THE PROJECT
The parties intend to explore and, if they proceed, carry out the following development work:
[PROJECT DESCRIPTION] (the "Project"). This Agreement governs confidentiality and ownership from
the first conversation onward, including any brief, benchmark, sample or brief-response already
exchanged before the Effective Date. It applies whether or not a fuller development or services
agreement follows.

2. CONFIDENTIAL INFORMATION
"Confidential Information" means any non-public information a party makes available to the other in
connection with the Project, in any form and whether or not marked confidential. The Company's
Confidential Information includes without limitation: the brief, target cost per unit, target retail
price and margin targets; target claims, nutrition and label ambitions; formulas, specifications,
benchmark selections and product concepts; ingredient, supplier and packaging preferences; launch
timing, channel plans and retailer conversations; and unreleased brand names, artwork and
positioning. The Developer's Confidential Information includes its own non-public technical
information, base systems and pricing disclosed for the Project.

Confidential Information includes anything disclosed orally or visually, including in a bench
session, a tasting or a call, whether or not confirmed in writing afterwards, and physical material
including bench samples, benchmarks, prototypes, pilot lots and retains.

Confidential Information does not include information the receiving party can demonstrate by
contemporaneous written records was already known to it without a duty of confidentiality, is or
becomes public through no act or omission of the receiving party, is rightfully received from a
third party without a duty of confidentiality, or was independently developed without use of or
reference to the disclosing party's Confidential Information.

3. CONFIDENTIALITY AND NON-USE
Each party shall hold the other's Confidential Information in confidence, shall not disclose it
except to its own personnel and advisors who need it for the Project and who are bound by written
obligations at least as protective as those in this Agreement, and shall use it only for the
Project. There is no residuals exception to this Agreement.

The Developer shall not use the Company's Confidential Information for any other client or for the
Developer's own account, and shall not disclose it to any laboratory, supplier or subcontractor
without the Company's prior written consent and written flow-down of these obligations. Each party
remains responsible for any breach by a person to whom it discloses.

4. OWNERSHIP OF DEVELOPMENT WORK PRODUCT
"Development Work Product" means everything conceived, created, developed or reduced to practice by
the Developer, alone or with others, in the course of or arising from the Project, including without
limitation: formulas, formulations, prototype and final recipes; ingredient selections, usage rates
and order of addition; process parameters and scale-up instructions; raw material and finished good
specifications; shelf-life, stability, micro, sensory and analytical protocols and results;
benchmark analyses; bench notes, trial records, batch sheets and failed trials; and every
improvement, modification and derivative of any of the foregoing developed for the Company.

All formulas, specifications and improvements developed for the Company under the Project belong to
the Company. The Developer hereby irrevocably assigns to the Company all right, title and interest
worldwide in and to the Development Work Product, including all patent, copyright, trade secret and
other intellectual property rights in it, together with the right to sue for and recover damages for
past, present and future infringement or misappropriation. This is a present assignment, effective
as each item of Development Work Product is created, and not a promise to assign in the future.

The Developer shall, at the Company's request and expense, sign any further documents and take any
further actions the Company reasonably requires to obtain, perfect, record or enforce those rights,
and this obligation continues after the Project ends.

The Developer shall not use the Development Work Product for any other party, and shall not develop
for any other party a product that is derived from or approximates the Development Work Product.

5. DEVELOPER BACKGROUND MATERIAL
Schedule A lists the Developer's base systems, platform technology, proprietary bases and other
material the Developer owned or controlled before the Effective Date, or develops entirely
independently of the Project, that the Developer intends to incorporate into the Development Work
Product ("Background Material"). Background Material remains the Developer's property. Anything not
listed on Schedule A is not Background Material.

If any Background Material is incorporated into the Development Work Product, the Developer grants
the Company a non-exclusive, worldwide, perpetual, irrevocable, fully paid, sublicensable and
transferable licence to make, have made, use, sell and offer for sale products incorporating that
Background Material, including through any co-manufacturer the Company selects. The Developer shall
tell the Company, before the first bench trial in which it is used, if a Background Material item
can only be sourced from the Developer.

6. SAMPLES, NOTES AND RECORDS
The Developer shall maintain complete bench notes and trial records for the Project and shall
deliver them to the Company, together with all formulas, specifications and analytical results, on
completion of the Project or on the Company's request. Within [RETURN DAYS] days after the Company's
written request, the Developer shall deliver to the Company or destroy, at the Company's election,
all bench samples, benchmarks, prototypes, pilot lots and retains relating to the Project, and shall
confirm in writing what it delivered and what it destroyed. The Developer shall not provide any
sample or retain to any third party, and shall not serve, taste-test or display any Project sample
to any other client.

7. IF THE PROJECT STOPS EARLY
If the Project is paused, abandoned or ends before a fuller agreement is signed, Sections 3, 4 and 6
still apply to everything created up to that point. Development Work Product created before the stop
belongs to the Company, and the Developer shall deliver it in the state it is in, including
incomplete formulas and failed trials, on payment of amounts properly due for work performed.
Confidentiality survives whether or not any project proceeds.

8. PARALLEL WORK
For [EXCLUSIVITY MONTHS] months after the Effective Date, the Developer shall not accept or perform
work for any other party on a brief that is substantially the same as the Project brief in the
[PRODUCT CATEGORY] category. Outside that restriction, and subject always to Sections 3 and 4,
nothing in this Agreement prevents the Developer from working in the category generally.

9. CONSIDERATION
The Company shall pay the Developer [PROJECT FEE] for the work covered by this Agreement. The
assignment in Section 4 is supported by that consideration and is not conditional on the Company's
payment of any later invoice; unpaid amounts are a debt owed to the Developer and are not a basis
for withholding, revoking or re-taking ownership of Development Work Product.

10. FULL AGREEMENT TO FOLLOW
The parties intend to sign a fuller development or services agreement covering scope, milestones,
fees, testing, regulatory and label responsibilities, and manufacturing transfer before substantial
work begins. Until they do, this Agreement governs confidentiality and ownership, and it does not
obligate either party to proceed with the Project.

11. TERM, SURVIVAL AND REMEDIES
This Agreement begins on the Effective Date and continues until the parties sign a fuller agreement
that supersedes it or either party terminates it on written notice. The confidentiality and non-use
obligations survive for [CONFIDENTIALITY YEARS] years after the date of each disclosure, and
continue for as long as the information remains a trade secret with respect to any formula,
formulation, process parameter or other trade secret material. Sections 4 through 7 survive
indefinitely. Each party acknowledges that a breach may cause harm for which monetary damages alone
may be an inadequate remedy, and that the non-breaching party may seek equitable relief in addition
to any other remedy available to it.

12. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

13. GENERAL
This Agreement is the entire agreement between the parties regarding confidentiality and ownership
of the Development Work Product and supersedes all prior discussions on those subjects. It may be
amended only in a writing signed by both parties. The Developer may not assign this Agreement
without the Company's prior written consent; the Company may assign it to a successor in connection
with a merger or a sale of substantially all of its assets. If any provision is held unenforceable,
the remainder continues in effect. This Agreement may be signed in counterparts, including by
electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [DEVELOPER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — DEVELOPER BACKGROUND MATERIAL
─────────────────────────────────────────────────────────────────────────────
List each item of the Developer's pre-existing or independently developed material that will be
used in the Project, with enough detail to identify it. Write "None" if there is none. An unlisted
item is not Background Material.

[BACKGROUND MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Developer initials: __________          Company initials: __________
`,
  },

  {
    id: "ip-assignment-agreement",
    docId: "ip-assignment-agreement",
    title: "INTELLECTUAL PROPERTY ASSIGNMENT AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Something of value was created by a person rather than by the company and needs to end up owned by the company: the brand name, the logo, the label artwork, the website, the photography, the formulation, the packaging structure.",
      whyItMatters:
        "Confidentiality controls who may speak. Assignment decides who owns. They are different documents and only one of them survives diligence. The expensive version of this gap is a brand where the founder personally registered the trademark, a freelance designer holds the artwork and a formulator holds the recipe — three owners of one product, discovered by an acquirer's counsel who then discounts the price or holds part of it back until every chain of title is clean.",
      negotiateCarefully: [
        {
          point: "Present assignment, not an agreement to assign",
          detail:
            "'Assignor will assign' leaves the company holding a contract right instead of the asset, and it depends on the assignor still being cooperative years later. The words that matter are that the assignor assigns now.",
        },
        {
          point: "Schedule the assets specifically",
          detail:
            "'All intellectual property relating to the business' is not a description anyone can record against a registration. List the marks, the artwork files, the formulations, the domains and the accounts, one line each, with enough detail that a stranger could identify them.",
        },
        {
          point: "The excluded-material schedule",
          detail:
            "A blank or vague exclusions schedule invites a later claim that a core element was pre-existing and never transferred. Fill it in, or write 'None' and have the assignor sign under it.",
        },
        {
          point: "Consideration and timing",
          detail:
            "An assignment signed long after the work with nothing given in exchange is the version most likely to be challenged. State what the assignor is receiving, even where it is nominal or is the compensation already paid.",
        },
        {
          point: "Further assurances and delivery of files",
          detail:
            "Recording a trademark or copyright assignment usually needs extra signatures later. Owning a label without the layered file and the dieline means paying to rebuild an asset you already own — make file delivery an obligation, not a courtesy.",
        },
      ],
      redFlags: [
        "Language that only promises a future assignment.",
        "A licence back that lets the creator reuse the work commercially.",
        "No warranty that the assignor owns the material or that it does not incorporate third-party assets.",
        "Stock imagery, fonts or illustrations inside the deliverables with no licence documentation.",
        "Refusal to hand over layered files, dielines, domain and account credentials, or bench records.",
        "A schedule of assigned assets written as one line saying 'all IP'.",
      ],
      timeToComplete: "30 minutes to fill, mostly building the schedules · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[ASSIGNOR NAME]", label: "Assignor's legal name", example: "Dana Reyes" },
      { token: "[ASSIGNOR TYPE]", label: "Assignor's description (individual, or entity type and state)", example: "an individual" },
      { token: "[ASSIGNOR ADDRESS]", label: "Assignor's address", example: "18 Bell Ave, Portland, OR 97209" },
      { token: "[ASSIGNED IP]", label: "Assigned assets (Schedule A), one line each", example: "Word mark ACME; label artwork v3 (layered .ai); flavour formulation ACM-04" },
      { token: "[EXCLUDED MATERIALS]", label: "Retained or pre-existing material (Schedule B), or 'None'", example: "None" },
      { token: "[THIRD PARTY MATERIALS]", label: "Third-party assets inside the assigned material (Schedule C)", example: "Font: Sabon (packaging licence, transferable)" },
      { token: "[CONSIDERATION]", label: "What the assignor receives", example: "the compensation previously paid for the work and other good and valuable consideration" },
      { token: "[DELIVERY DAYS]", label: "Days to deliver files and records", example: "15" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Intellectual Property Assignment Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [ASSIGNOR NAME], [ASSIGNOR TYPE], with an address at [ASSIGNOR ADDRESS] (the "Assignor"); and

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company").

BACKGROUND
The Assignor created, developed, acquired or holds the intellectual property described on
Schedule A, which the Company uses or intends to use in its business. The Assignor is transferring
that intellectual property to the Company on the terms set out below.

1. ASSIGNED INTELLECTUAL PROPERTY
"Assigned IP" means the intellectual property identified on Schedule A, together with:
(a) all works of authorship, designs, artwork, illustrations, photographs, video, audio, copy,
    software and websites included in or created for it, and all copyrights in them;
(b) all trademarks, service marks, trade names, brand names, logos, trade dress and domain names
    included in it, together with the goodwill of the business symbolised by them;
(c) all inventions, formulations, recipes, specifications, process know-how, bench records and
    trade secrets included in it;
(d) all registrations and applications for any of the foregoing, in any country;
(e) all files, source materials and records described in Section 5; and
(f) all rights to sue for and recover damages, profits and other relief for any past, present or
    future infringement, misappropriation or dilution of any of the foregoing.

Assigned IP does not include the material identified on Schedule B (the "Excluded Material").
Anything not listed on Schedule B is not Excluded Material.

2. PRESENT ASSIGNMENT
For the consideration described in Section 3, the Assignor hereby irrevocably sells, assigns,
transfers and conveys to the Company, and the Company accepts, all right, title and interest
worldwide in and to the Assigned IP, free of any lien, licence, encumbrance or claim. This is a
present assignment effective on the Effective Date and is not an agreement to assign at a later
date.

To the extent any right in the Assigned IP cannot be assigned as a matter of law, the Assignor
grants the Company an exclusive, worldwide, perpetual, irrevocable, fully paid, sublicensable and
transferable licence to use, reproduce, modify, distribute, publicly display, publicly perform and
otherwise exploit that right for any purpose. To the extent permitted, the Assignor waives, and
agrees not to assert against the Company, its successors or its licensees, any moral rights or
rights of attribution or integrity in the Assigned IP.

The Assignor retains no right to use the Assigned IP and receives no licence back under this
Agreement, except any limited permission expressly stated on Schedule B.

3. CONSIDERATION
In exchange for the assignment, the Company is providing the Assignor with [CONSIDERATION], receipt
and sufficiency of which the Assignor acknowledges. The assignment is effective on the Effective
Date and is not conditional on any later payment; any unpaid amount is a debt and is not a basis for
withholding, revoking or re-taking the Assigned IP.

4. FURTHER ASSURANCES
The Assignor shall, promptly on the Company's request and at the Company's expense, sign, deliver
and, where required, have notarised any document and take any further action the Company reasonably
requires to obtain, perfect, record, maintain, defend or enforce the Company's rights in the
Assigned IP anywhere in the world, including short-form assignments in recordable form, declarations
and applications, and shall provide reasonable cooperation and testimony in any proceeding
concerning the Assigned IP. This obligation binds the Assignor and its heirs, executors, successors
and assigns and continues after the Effective Date without limit of time.

If the Company is unable after reasonable effort to obtain the Assignor's signature on any such
document, the Assignor appoints the Company as its agent and attorney-in-fact, coupled with an
interest, solely to sign that document on the Assignor's behalf for the purposes of this Section.

5. DELIVERY OF FILES AND RECORDS
Within [DELIVERY DAYS] days after the Effective Date, the Assignor shall deliver to the Company all
originals and copies of material comprising or needed to use the Assigned IP, in native editable
form where it exists, including: layered and working design files; dielines and structural files;
packaged or listed fonts; raw and original camera files, editing project files and 3D or render
files; code repositories and website credentials; domain registrar and account access; formulation
sheets, bench notes, trial records and specifications; and all registration certificates,
correspondence and prosecution files. Delivery of flattened or exported files alone does not satisfy
this Section.

6. THIRD-PARTY MATERIAL
Schedule C lists every third-party font, stock image, illustration, music track, plug-in or other
licensed asset embedded in or required to use the Assigned IP, the licence that covers it and who
holds that licence. The Assignor represents that each such licence permits the Company's intended
use, including reproduction on packaging and use in paid advertising, and shall assign or, where the
licence does not permit assignment, use reasonable efforts to help the Company obtain its own
licence.

7. ASSIGNOR REPRESENTATIONS AND WARRANTIES
The Assignor represents and warrants that:
(a) it has full right, power and authority to enter into this Agreement and to make the assignment;
(b) it is the sole and exclusive owner of the Assigned IP, and the Assigned IP is free of any lien,
    security interest, licence, option or other encumbrance;
(c) the Assigned IP is original to the Assignor except for the Excluded Material and the
    third-party material listed on Schedule C;
(d) it has not assigned, licensed or granted any right in the Assigned IP to any other party;
(e) any employee, contractor or subcontractor who contributed to the Assigned IP has assigned their
    rights to the Assignor in writing; and
(f) to the best of the Assignor's knowledge, the Assigned IP does not infringe or misappropriate
    the rights of any third party, and the Assignor has received no claim or notice alleging that it
    does.

8. CONFIDENTIALITY
The Assignor shall keep confidential, and shall not use for itself or any other party, all
non-public information included in or relating to the Assigned IP, including formulations, bench
records, specifications, costs and supplier identities. This obligation continues after the
Effective Date and, with respect to trade secret material, for as long as the information remains a
trade secret.

9. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

10. GENERAL
This Agreement is the entire agreement between the parties regarding its subject matter and
supersedes all prior discussions. It may be amended only in a writing signed by both parties. It
binds and benefits the parties and their respective heirs, successors and permitted assigns, and the
Company may assign it, including to a successor in connection with a merger or a sale of
substantially all of its assets. If any provision is held unenforceable, the remainder continues in
effect. This Agreement may be signed in counterparts, including by electronic signature, each of
which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


ASSIGNOR                                  COMPANY

[ASSIGNOR NAME]                           [COMPANY NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — ASSIGNED INTELLECTUAL PROPERTY
─────────────────────────────────────────────────────────────────────────────
Identify each asset specifically enough that someone outside the deal could tell what it is:
brand names and marks (with any application or registration number and the goods covered), artwork
and design files (with file names and versions), photography and video, formulations and
specifications (with internal codes), domain names, social accounts and code repositories.

[ASSIGNED IP]

_____________________________________________________________________________

_____________________________________________________________________________

_____________________________________________________________________________

Assignor initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — EXCLUDED MATERIAL
─────────────────────────────────────────────────────────────────────────────
Material the Assignor is keeping, and any limited permission the Company has to use it. Write
"None" if there is none. An unlisted item is not excluded.

[EXCLUDED MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Assignor initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — THIRD-PARTY MATERIAL AND LICENCES
─────────────────────────────────────────────────────────────────────────────
Every third-party font, image, illustration, music track or other licensed asset inside or needed
for the Assigned IP, the licence that covers it, who holds it, and whether it may be transferred.

[THIRD PARTY MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Assignor initials: __________          Company initials: __________
`,
  },

  {
    id: "work-for-hire-agreement",
    docId: "work-for-hire-agreement",
    title: "WORK MADE FOR HIRE AND ASSIGNMENT AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are commissioning creative work you intend to own from the moment it exists: label artwork, brand identity, product photography, video, website design, illustration or copy.",
      whyItMatters:
        "Founders treat 'work for hire' as a magic phrase that makes everything theirs. It is a defined concept in copyright with limits on which kinds of commissioned work it reaches, which is exactly why professionally drafted agreements always pair it with an outright assignment. A packaging project produces artwork, photography, a dieline, copy and a font pairing, and those pieces do not sit neatly inside one characterisation. Rely on the label alone and whatever it does not reach stays with the creator — usually discovered at the printer's deadline.",
      negotiateCarefully: [
        {
          point: "Both mechanisms, in the same document",
          detail:
            "Work-for-hire characterisation first, present assignment second, covering anything the first does not reach. An agreement with only one of the two has a gap you will not find until you need to prove ownership.",
        },
        {
          point: "Define deliverables as files, not outcomes",
          detail:
            "'Final files' commonly means flattened exports. Name the layered artwork, the dielines, the packaged fonts, the raw camera files, the editing project files and the 3D scenes. Owning an image you cannot edit is the most common form of half-ownership in CPG.",
        },
        {
          point: "Ownership should not hang on final payment",
          detail:
            "A clause making ownership conditional on payment in full turns a billing dispute into an ownership dispute at exactly the wrong moment. Move ownership to creation or delivery and hold a retention instead if you want leverage.",
        },
        {
          point: "Fonts, stock and everything else borrowed",
          detail:
            "A label built on a licensed font or a stock illustration is only as transferable as those underlying licences allow, and desktop licences frequently do not cover mass-reproduced packaging. Get the list, get the licences, and check they reach packaging and paid media.",
        },
        {
          point: "Subcontractors and portfolio rights",
          detail:
            "The illustrator or retoucher the studio hired is the actual creator, and their rights only reach you if the studio was required to secure them. Separately, unrestricted portfolio rights can put unreleased packaging on a public site before you ship.",
        },
      ],
      redFlags: [
        "Work-for-hire language with no assignment fallback.",
        "Ownership conditional on payment in full.",
        "No disclosure of fonts, stock or third-party illustration used in the deliverables.",
        "Deliverables defined as final exports only, with no source or working files.",
        "A creator who reserves the right to reuse elements of the design for other clients.",
        "No obligation to secure rights from the subcontractors who will actually do the work.",
      ],
      timeToComplete: "25 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[CREATOR NAME]", label: "Creator's or studio's legal name", example: "Rill Design Studio LLC" },
      { token: "[CREATOR ADDRESS]", label: "Creator's address", example: "18 Bell Ave, Portland, OR 97209" },
      { token: "[PROJECT DESCRIPTION]", label: "The commissioned project", example: "brand identity and primary label artwork for a four-SKU canned tea line" },
      { token: "[DELIVERABLES]", label: "Deliverables (Schedule A), named as files", example: "logo suite, four label artworks, dieline, layered .ai source, packaged fonts, print-ready PDFs" },
      { token: "[SOURCE FILE FORMATS]", label: "Required source and working file formats", example: "layered .ai and .psd, .indd packages, camera RAW, .prproj project files" },
      { token: "[DELIVERY DATE]", label: "Delivery date", example: "October 15, 2026" },
      { token: "[FEE]", label: "Fee for the work", example: "the fee set out in the parties' statement of work" },
      { token: "[PUBLIC LAUNCH DATE]", label: "Date portfolio use may begin", example: "the public launch date of the Product" },
      { token: "[THIRD PARTY MATERIALS]", label: "Third-party fonts, stock and assets (Schedule B)", example: "Font: Sabon (desktop + packaging licence, held by Creator)" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Work Made for Hire and Assignment Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [CREATOR NAME], with an address at [CREATOR ADDRESS] (the "Creator").

1. THE COMMISSION
The Company commissions the Creator to create the following: [PROJECT DESCRIPTION] (the "Project").
The Creator shall deliver the items listed on Schedule A (the "Deliverables") by [DELIVERY DATE].
The Company is commissioning the Deliverables specifically for its own use, and the Creator is
creating them to the Company's order and specification.

2. WORK MADE FOR HIRE
The parties intend that the Deliverables and all other Work Product be owned by the Company from the
moment of creation. To the fullest extent permitted by applicable copyright law, the Deliverables
and all other Work Product are "works made for hire" created for the Company, and the Company is the
author and owner of all rights in them.

3. ASSIGNMENT AS A BACKSTOP
To the extent any Work Product does not qualify as a work made for hire, or the characterisation in
Section 2 is for any reason ineffective, the Creator hereby irrevocably assigns to the Company all
right, title and interest worldwide in and to that Work Product, including all copyrights,
trademark rights, patent rights, trade secret rights and other intellectual property rights in it,
together with the right to sue for and recover damages for any past, present or future infringement
or misappropriation. This is a present assignment, effective as each item of Work Product is
created, and is not a promise to assign in the future. Ownership does not depend on the Deliverables
qualifying as works made for hire.

"Work Product" means everything the Creator conceives, creates, designs, writes, photographs, films,
records or produces, alone or with others, in the course of or arising from the Project, including
the Deliverables and all drafts, concepts, sketches, storyboards, rejected routes, outtakes and
working files created along the way.

To the extent any right cannot be assigned as a matter of law, the Creator grants the Company an
exclusive, worldwide, perpetual, irrevocable, fully paid, sublicensable and transferable licence to
use, reproduce, modify, distribute, publicly display, publicly perform and otherwise exploit that
right for any purpose. To the extent permitted, the Creator waives, and agrees not to assert against
the Company or its licensees, any moral rights or rights of attribution or integrity in the Work
Product.

4. SOURCE, WORKING AND RAW FILES
The Deliverables include the editable material, not only the exports. On delivery, and at any time
on the Company's request, the Creator shall provide the Work Product in native editable form in the
following formats: [SOURCE FILE FORMATS], including without limitation:
(a) layered design files with live type, linked assets and all artboards;
(b) packaging dielines and structural files matching the converter's specification, and
    print-ready files with fonts outlined or packaged;
(c) the packaged font files actually used, or a written list identifying each font and its licence;
(d) raw and original camera files, including outtakes and unselected frames, and the colour
    profiles or LUTs applied;
(e) video and audio editing project files, sequences, graphics templates, stems and masters;
(f) 3D scene files, models, textures and render settings; and
(g) code repositories, content management access and website build files.
Delivery of flattened, exported or watermarked files alone does not constitute delivery.

5. PAYMENT AND OWNERSHIP TIMING
The Company shall pay the Creator [FEE] on the terms set out in the parties' statement of work or
invoice. Ownership of the Work Product vests in the Company as it is created under Sections 2 and 3
and is not conditional on payment. Any unpaid amount is a debt owed to the Creator and is not a
basis for withholding, revoking or re-taking ownership of, or access to, the Work Product.

6. THIRD-PARTY MATERIAL
Schedule B lists every third-party font, stock image, illustration, music track, plug-in, template
or other licensed asset embedded in or required to use the Deliverables, the licence covering it and
who holds that licence. The Creator represents that each licence permits the Company's intended use,
which includes reproduction on primary and secondary packaging, retailer and marketplace listings,
trade materials, paid advertising and out-of-home. The Creator shall not incorporate any third-party
material not listed on Schedule B without the Company's prior written consent, and shall not embed
any asset whose licence prohibits the uses named in this Section.

7. SUBCONTRACTORS
The Creator shall not engage any subcontractor, studio, illustrator, retoucher, photographer,
editor, model or other third party for the Project without the Company's prior written consent.
Before any approved subcontractor begins work, the Creator shall bind them in writing to
confidentiality obligations and to work-for-hire and assignment obligations at least as protective
as Sections 2 and 3, and shall secure for the Company all rights that subcontractor creates. On
request the Creator shall identify every person who worked on the Project and provide evidence of
those written obligations. The Creator remains responsible for its subcontractors.

8. RELEASES
Where the Work Product includes any identifiable person, private property or location, the Creator
shall obtain signed model, property and location releases in a form the Company approves, covering
the uses named in Section 6, and shall deliver copies to the Company with the Deliverables.

9. WARRANTIES
The Creator represents and warrants that: it has full authority to enter into this Agreement and is
not bound by any obligation that conflicts with it; the Work Product is original to the Creator
except for the material listed on Schedule B; the Work Product does not, to the best of the
Creator's knowledge, infringe or misappropriate the rights of any third party; and it has not
granted and will not grant any right in the Work Product to any other party.

10. PORTFOLIO AND CREDIT
The Creator may display finished Deliverables in its portfolio and self-promotional materials only
on and after [PUBLIC LAUNCH DATE], and only in a form that does not disclose any formulation,
ingredient, cost, supplier, sales or forecast information. The Company is not required to give the
Creator credit on any use, and no credit is required on packaging, retailer listings, trade
materials or paid advertising. The Creator shall remove any portfolio display on the Company's
written request.

11. CONFIDENTIALITY AND EMBARGO
The Creator shall keep confidential all non-public information of the Company it receives, shall use
it only for the Project, and shall not publish, post or show any Work Product, concept or unreleased
packaging before [PUBLIC LAUNCH DATE] without the Company's prior written consent. This obligation
continues after the Project ends and, with respect to trade secret material, for as long as the
information remains a trade secret.

12. FURTHER ASSURANCES
The Creator shall, promptly on the Company's request and at the Company's expense, sign any document
and take any action the Company reasonably requires to obtain, perfect, record or enforce its rights
in the Work Product, including assignments in recordable form. This obligation continues after
delivery. If the Company is unable after reasonable effort to obtain the Creator's signature, the
Creator appoints the Company as its agent and attorney-in-fact, coupled with an interest, solely to
sign such documents on the Creator's behalf.

13. INDEPENDENT CONTRACTOR
The Creator is an independent contractor. Nothing in this Agreement creates an employment,
partnership, joint venture or agency relationship, and this Agreement does not obligate the Company
to commission further work.

14. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

15. GENERAL
This Agreement is the entire agreement between the parties regarding its subject matter and
supersedes all prior discussions. Where it conflicts with a statement of work, proposal or invoice,
this Agreement controls on ownership, files, third-party material and confidentiality. It may be
amended only in a writing signed by both parties. The Creator may not assign this Agreement without
the Company's prior written consent; the Company may assign it to a successor in connection with a
merger or a sale of substantially all of its assets. If any provision is held unenforceable, the
remainder continues in effect. This Agreement may be signed in counterparts, including by electronic
signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CREATOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — DELIVERABLES
─────────────────────────────────────────────────────────────────────────────
List each deliverable as a file, not as an outcome. Include quantity, format, dimensions and the
source files that go with each item.

[DELIVERABLES]

_____________________________________________________________________________

_____________________________________________________________________________

Creator initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — THIRD-PARTY MATERIAL AND LICENCES
─────────────────────────────────────────────────────────────────────────────
Every third-party font, image, illustration, music track, plug-in or template inside or needed for
the Deliverables, the licence covering it, who holds it, and whether it covers packaging and paid
media. Write "None" if there is none.

[THIRD PARTY MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Creator initials: __________          Company initials: __________
`,
  },

  {
    id: "content-release",
    docId: "content-release",
    title: "CONTENT LICENCE AND RELEASE (PHOTOGRAPHY AND VIDEO)",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before any paid shoot, and before using content a creator produced for you — including content shot on a phone at an event, in a kitchen or on a plant floor.",
      whyItMatters:
        "This is the permission side of the ownership question, and CPG founders get caught by it constantly. The standard photography deal is a licence, not a transfer, and licences are written channel by channel. Content licensed for organic social is routinely not licensed for paid ads, marketplace listing images, retailer portals, distributor sell sheets or packaging. The brand runs the image as an ad because the file is on its own drive, and the first it hears about the limit is an invoice or a takedown from the photographer's agent.",
      negotiateCarefully: [
        {
          point: "Name every channel, including the unglamorous ones",
          detail:
            "Retailer portals, marketplace listing images, distributor sell sheets, shelf talkers and trade show graphics are where CPG content actually lives, and consumer-focused licences omit them constantly. A channel that is not named is not licensed.",
        },
        {
          point: "Paid media is its own grant",
          detail:
            "A licence covering 'social media' often excludes paid amplification, which is the use with real budget behind it. Say paid social, search, display, video, streaming and any boosted organic post, in words.",
        },
        {
          point: "Packaging, and the term that outlives the campaign",
          detail:
            "On-pack use is almost always a separate, higher-priced grant, and an expired grant still reaches shelf on printed inventory. Buy an assignment or a perpetual grant for hero and packaging imagery even if the lifestyle library stays on a licence.",
        },
        {
          point: "Term versus the life of the SKU",
          detail:
            "A one-year term on hero product photography means your listing images expire while the SKU is still selling. Tie the term to the life of the pack generation, or make it perpetual and pay once.",
        },
        {
          point: "Raw files and the right to edit",
          detail:
            "Every retailer template crops differently and every flavour extension recolours. Without raw files and an express right to edit, crop, retouch and create derivatives, each small change means going back to the shooter.",
        },
      ],
      redFlags: [
        "A licence silent on paid media.",
        "A term shorter than the expected life of the SKU.",
        "No obligation on the shooter to obtain and deliver model, property and location releases.",
        "Packaging use excluded or simply unaddressed.",
        "Mandatory credit on every use, including packaging and paid ads.",
        "No right to edit or crop, and delivery of selects only with no raw material.",
      ],
      timeToComplete: "20 minutes to fill · sign before the shoot day",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[CREATOR NAME]", label: "Photographer's or videographer's legal name", example: "June Okafor Photography LLC" },
      { token: "[CREATOR ADDRESS]", label: "Creator's address", example: "9 Mill Rd, Brooklyn, NY 11222" },
      { token: "[SHOOT DATE]", label: "Shoot date or dates", example: "September 12–13, 2026" },
      { token: "[SHOOT LOCATION]", label: "Shoot location", example: "Studio 4, Brooklyn, NY" },
      { token: "[CONTENT DESCRIPTION]", label: "What is being shot (Schedule A)", example: "product-on-white for four SKUs, plus lifestyle stills and two 15-second vertical videos" },
      { token: "[EXCLUSIVITY]", label: "Exclusive or non-exclusive", example: "exclusive" },
      { token: "[LICENCE TERM]", label: "Length of the general licence", example: "perpetual" },
      { token: "[PACKAGING TERM]", label: "Length of the packaging grant", example: "perpetual" },
      { token: "[TERRITORY]", label: "Territory", example: "worldwide" },
      { token: "[FEE]", label: "Fee for the shoot and the rights granted", example: "the fee set out in the parties' estimate dated September 1, 2026" },
      { token: "[EXTENSION FEE]", label: "Agreed price to extend or expand the grant later", example: "a pre-agreed extension fee stated in Schedule A" },
      { token: "[CREDIT LINE]", label: "Credit line, where credit is given", example: "Photography by June Okafor" },
      { token: "[DELIVERY DAYS]", label: "Days to deliver files after the shoot", example: "14" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Content Licence and Release (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [CREATOR NAME], with an address at [CREATOR ADDRESS] (the "Creator").

1. THE CONTENT
The Creator is producing photography, video and audio for the Company on [SHOOT DATE] at
[SHOOT LOCATION], consisting of [CONTENT DESCRIPTION] and further described on Schedule A (the
"Content"). The Content includes all still images, video, audio, motion assets and derivatives
created for the Company, whether or not selected for delivery.

2. GRANT OF RIGHTS
The Creator grants the Company an [EXCLUSIVITY], [LICENCE TERM], [TERRITORY], irrevocable, fully
paid, royalty-free, sublicensable and transferable licence to use, reproduce, distribute, publicly
display, publicly perform, edit, adapt and create derivative works from the Content for the
Permitted Uses in Section 3. The Company may exercise these rights itself and through its
distributors, retailers, brokers, agencies, contractors and licensees.

3. PERMITTED USES
The Permitted Uses are, without limitation:
(a) the Company's own websites, apps, email, blogs and organic social media;
(b) paid advertising in every medium, including paid social, boosted organic posts, search,
    display, native, video, connected television and streaming, audio and podcast, print and
    cinema;
(c) online marketplace listings and retailer e-commerce portals, including listing images,
    secondary images, A-plus and enhanced content modules, video modules and syndicated content
    feeds;
(d) retailer and distributor systems and materials, including new item forms, item setup and
    content syndication portals, category review presentations and planogram submissions;
(e) distributor and broker sell sheets, sales decks, price lists, catalogues, order guides and
    trade advertising;
(f) in-store and shopper marketing, including shelf talkers, case cards, displays, coolers, danglers
    and demo materials;
(g) out-of-home, including billboards, transit, street furniture, vehicle wraps, airport, arena and
    venue signage, and event and trade show graphics;
(h) packaging and labelling, on the terms in Section 4;
(i) press, editorial, public relations and awards submissions; and
(j) internal, investor, lender and diligence materials.

The Company may crop, resize, retouch, colour-correct, composite, caption, overlay text and claims,
translate, localise, re-edit and create derivative works from the Content for these uses, and may
use the Content in combination with other material.

4. PACKAGING AND LABELLING
Packaging is treated separately because it is effectively permanent. The Creator grants the Company
a [PACKAGING TERM], [TERRITORY] right to use the Content on and in connection with primary
packaging, secondary packaging, shippers, cases, closures, sleeves, cartons, inserts and labels for
the Company's products, including on later label revisions and successor pack generations.

If the packaging grant is for a fixed period rather than perpetual, the Company may continue to
manufacture, ship and sell packaging already printed or in production when the period ends, and its
distributors and retailers may continue to sell that inventory through, without further payment.

5. RESERVED RIGHTS AND EXCLUSIVITY
The Creator retains ownership of the copyright in the Content except for any item identified on
Schedule A as assigned to the Company, which is assigned outright. Where the grant in Section 2 is
exclusive, the Creator shall not licence, sell, syndicate, submit to a stock library or otherwise
make the Content available to any other party, and specifically not to any competitor of the
Company, during the term of the licence. The Creator may display the Content in its portfolio and
self-promotional materials, in a form that does not disclose any unreleased product, formulation,
cost, supplier or sales information, and shall remove any display on the Company's written request.

6. DELIVERY OF FILES
Within [DELIVERY DAYS] days after the shoot the Creator shall deliver: all selected and edited
images and video in the formats the Company specifies; all raw and original camera files, including
outtakes and unselected frames; audio stems and masters; editing project files, sequences and
graphics templates; and the colour profiles or LUTs applied. Delivery of flattened or watermarked
selects alone does not satisfy this Section. The Creator shall retain a backup of the raw material
for a reasonable period and provide copies on request at the Company's cost.

7. RELEASES AND CLEARANCES
The Creator shall obtain, before the Content is captured, signed model, property and location
releases from every identifiable person, and for every private property, venue and location
appearing in the Content, each covering the Permitted Uses in Section 3 and the packaging use in
Section 4, and shall deliver copies to the Company with the Content. The Creator shall identify any
third-party product, artwork, signage, trademark or music appearing in the Content and shall not
include any such material without the Company's prior written consent.

8. WARRANTIES
The Creator represents and warrants that: it has full authority to grant the rights in this
Agreement; the Content is original to the Creator and does not, to the best of the Creator's
knowledge, infringe or misappropriate the rights of any third party; it has obtained the releases
required by Section 7; and it has not granted and will not grant any right in the Content that
conflicts with this Agreement.

9. CREDIT
Where the Company chooses to give credit, it will use [CREDIT LINE]. Credit is not a condition of
any Permitted Use, and no credit is required on packaging, retailer or marketplace listings, trade
materials, in-store materials, out-of-home or paid advertising.

10. FEE AND FUTURE EXTENSIONS
The Company shall pay the Creator [FEE] for the shoot and the rights granted. If the Company later
wishes to extend the term, expand the territory or add a use beyond those granted here, the price is
[EXTENSION FEE], agreed now rather than at renewal. The rights granted in this Agreement are not
conditional on any later payment; any unpaid amount is a debt owed to the Creator and is not a basis
for revoking, suspending or narrowing the licence.

11. CONFIDENTIALITY
The Content may show unreleased product, packaging, facilities and processes. The Creator shall keep
all non-public information it sees confidential, shall not publish or post any Content or
behind-the-scenes material without the Company's prior written consent, and shall comply with any
confidentiality obligations of a facility where a shoot takes place.

12. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

13. GENERAL
This Agreement is the entire agreement between the parties regarding the Content and supersedes all
prior discussions, including any terms printed on an estimate, invoice or delivery note. It may be
amended only in a writing signed by both parties. The Company may assign this Agreement and the
licence granted under it, including to a successor in connection with a merger or a sale of
substantially all of its assets. If any provision is held unenforceable, the remainder continues in
effect. This Agreement may be signed in counterparts, including by electronic signature, each of
which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CREATOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — THE CONTENT
─────────────────────────────────────────────────────────────────────────────
Describe the shoot and what is being delivered: SKUs and set-ups, number of images and videos,
formats and aspect ratios, whether any item is assigned outright rather than licensed, and any
agreed extension pricing.

[CONTENT DESCRIPTION]

_____________________________________________________________________________

_____________________________________________________________________________

Creator initials: __________          Company initials: __________
`,
  },

  {
    id: "model-release",
    docId: "model-release",
    title: "MODEL AND LIKENESS RELEASE",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Anyone recognisable is about to appear in content you will use commercially — a paid model, a friend on a shoot day, an employee, a founder, a customer at a sampling event, a child in a family scene.",
      whyItMatters:
        "The release that covers a shoot rarely covers a package. Putting a person's face on primary packaging is a different and far more permanent use than running them in a social post, and it is the use most short releases silently omit. Once artwork prints, a limited release means either a relabel or product you cannot ship. The same gap shows up with paid media, and with any content that makes the person look like they are endorsing the product rather than simply appearing in a photograph.",
      negotiateCarefully: [
        {
          point: "Packaging is a separate decision",
          detail:
            "Advertising can be pulled down in an afternoon; a pack runs until the inventory sells through. Grant packaging use explicitly, with a term that outlasts the print run, and price it separately if the person expects to be paid for it.",
        },
        {
          point: "Endorsement versus appearance",
          detail:
            "There is a real difference between being in a photograph and appearing to recommend the product. Say which is permitted, and match it to how the content will actually run — a consumption shot beside a functional claim reads as endorsement whatever the release says.",
        },
        {
          point: "Revocation and printed inventory",
          detail:
            "A release the individual can withdraw at will creates a problem for printed packaging and for media already placed. If any withdrawal right is agreed, give it a notice period and a sell-through window for inventory and booked media.",
        },
        {
          point: "Minors",
          detail:
            "Content featuring a child needs a parent or guardian signature and usually a narrower scope. Decide before the shoot whether that child will appear on pack, because renegotiating after artwork is approved is expensive.",
        },
        {
          point: "The right to edit and composite",
          detail:
            "Retouching, cropping to a retailer template, compositing into a new background or placing the image next to a claim can all fall outside a narrowly drawn release. Ask for editing rights in the same breath as the media grant.",
        },
      ],
      redFlags: [
        "A release limited to one campaign when the image is destined for packaging.",
        "A withdrawal right with no notice period and no wind-down for printed inventory.",
        "No parent or guardian signature where a minor appears.",
        "Silence on paid media.",
        "An agency release that binds the agency but not the individual who appears.",
      ],
      timeToComplete: "5 minutes to fill · sign on the shoot day, before the camera comes out",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 12, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[INDIVIDUAL NAME]", label: "Name of the person appearing", example: "Dana Reyes" },
      { token: "[INDIVIDUAL ADDRESS]", label: "Their address", example: "18 Bell Ave, Portland, OR 97209" },
      { token: "[SHOOT DATE]", label: "Shoot date or dates", example: "September 12, 2026" },
      { token: "[SHOOT DESCRIPTION]", label: "What is being shot", example: "lifestyle and consumption stills for the launch of the canned tea line" },
      { token: "[ADVERTISING TERM]", label: "Length of the advertising and marketing grant", example: "perpetual" },
      { token: "[PACKAGING TERM]", label: "Length of the packaging grant", example: "perpetual" },
      { token: "[TERRITORY]", label: "Territory", example: "worldwide" },
      { token: "[ENDORSEMENT PERMITTED]", label: "Is endorsement permitted — yes or no", example: "yes" },
      { token: "[COMPENSATION]", label: "Compensation for the advertising and marketing grant", example: "the day rate agreed in writing before the shoot" },
      { token: "[PACKAGING COMPENSATION]", label: "Additional compensation for packaging use, if any", example: "no additional payment" },
      { token: "[WIND DOWN DAYS]", label: "Days to wind down after any agreed withdrawal", example: "90" },
      { token: "[GUARDIAN NAME]", label: "Parent or guardian's name, if the individual is a minor", example: "Leave blank if the individual is an adult" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Model and Likeness Release (this "Release") is given as of [EFFECTIVE DATE] by:

  [INDIVIDUAL NAME], of [INDIVIDUAL ADDRESS] (the "Individual"),

to:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company").

1. THE CONTENT
The Individual is appearing in photography, video and audio produced for or on behalf of the Company
on [SHOOT DATE], consisting of [SHOOT DESCRIPTION] (the "Content"), together with any still frame,
excerpt, edit or derivative of it.

2. WHAT THE INDIVIDUAL IS PERMITTING
The Individual grants the Company the right to use the Individual's image, likeness, photograph,
silhouette, voice, performance, first name and, where the Individual has agreed in writing, full
name and biographical details, as they appear in or relate to the Content, in the media described
in Sections 3 and 4. The Company may exercise this right itself and through its distributors,
retailers, brokers, agencies, contractors and licensees, and may assign it to a successor of the
business.

3. ADVERTISING AND MARKETING USE
For [ADVERTISING TERM], throughout [TERRITORY], the Company may use the Content in advertising and
marketing, including without limitation: the Company's websites, apps, email and organic social
media; paid advertising in every medium, including paid social, boosted posts, search, display,
video, connected television and streaming, audio, print and cinema; online marketplace listings and
retailer e-commerce portals; distributor and broker sell sheets, sales decks and trade advertising;
in-store and shopper materials, including shelf talkers, case cards and displays; out-of-home,
including billboards, transit and vehicle wraps; event, trade show and sampling materials; and
press, editorial and awards submissions.

4. PACKAGING AND LABELLING USE — READ THIS SECTION SEPARATELY
Packaging is a longer and more permanent use than advertising, and it is granted separately here so
the Individual can consider it on its own.

For [PACKAGING TERM], throughout [TERRITORY], the Company may use the Content on and in connection
with the primary packaging, secondary packaging, shippers, cases, sleeves, cartons, inserts and
labels of its products, including on later label revisions and successor pack generations.

The Individual understands that packaging is printed in advance and in quantity, that printed
packaging remains in the supply chain and on shelf for an extended period after it is produced, and
that packaging featuring the Individual cannot practically be withdrawn once printed. Accordingly,
if the packaging grant is for a fixed period, the Company may continue to manufacture, ship and sell
packaging already printed or in production when that period ends, and its distributors and retailers
may sell that inventory through, without further payment.

5. ENDORSEMENT
Whether the Company may present the Individual as endorsing, recommending or personally using the
products: [ENDORSEMENT PERMITTED]. If endorsement is not permitted, the Company may still show the
Individual appearing with, holding, preparing or consuming the product, but shall not attribute a
quotation, testimonial, recommendation or statement of personal results to the Individual without a
separate written agreement.

6. EDITING
The Company may crop, resize, retouch, colour-correct, composite, caption, overlay text and claims,
translate, re-edit and create derivative works from the Content, and may use it in combination with
other images, words and materials. The Individual waives any right to inspect or approve the
finished materials or the uses to which they are put, and any claim based on alteration or
composite use, except that the Company shall not use the Content in a way that is defamatory or that
depicts the Individual in a knowingly false light.

7. COMPENSATION
The Individual is receiving [COMPENSATION] for the rights granted in Section 3, and
[PACKAGING COMPENSATION] for the rights granted in Section 4, receipt and sufficiency of which the
Individual acknowledges. No further payment is due for any use within the scope of this Release, and
the Individual has no right to residuals, royalties or usage fees.

8. NO OBLIGATION TO USE
The Company is not required to use the Content, and nothing in this Release obligates the Company to
produce, publish or continue any material featuring the Individual.

9. WITHDRAWAL
This Release is irrevocable within the scope granted, except as follows. If the parties have agreed
in writing that the Individual may withdraw from future use, the Individual must give the Company
written notice, and the Company may continue for [WIND DOWN DAYS] days after that notice to run
media already booked or placed, and may continue indefinitely to manufacture, ship and sell
packaging already printed or in production and to allow its distributors and retailers to sell that
inventory through. The Company is not required to recall, destroy or replace any material already
produced, printed or distributed.

10. CONFIDENTIALITY
The shoot may involve unreleased product, packaging, formulations or facilities. The Individual
shall keep confidential everything non-public that they see or are told, and shall not post,
publish, describe or share any image, video or description of the shoot or the product before the
Company's public launch without the Company's prior written consent.

11. THE INDIVIDUAL'S CONFIRMATIONS
The Individual confirms that they are of legal age and able to sign this Release, or that a parent
or guardian is signing on their behalf under Section 12; that they have read and understood it,
including Section 4; that they are not bound by any agency, union or other agreement that conflicts
with it; and that they have had the opportunity to ask questions before signing.

12. MINORS
If the Individual is a minor under the law of the Individual's state of residence, this Release must
also be signed by a parent or legal guardian, [GUARDIAN NAME], who confirms that they are the
Individual's parent or legal guardian, that they have the authority to grant these rights on the
Individual's behalf, and that they agree to every term of this Release, including the packaging use
in Section 4.

13. GOVERNING LAW AND VENUE
This Release is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

14. GENERAL
This Release is the entire agreement between the parties regarding the use of the Individual's
likeness in the Content and supersedes all prior discussions. It may be amended only in a writing
signed by both parties. It binds the Individual and their heirs, executors and assigns and benefits
the Company and its successors, licensees and assigns. If any provision is held unenforceable, the
remainder continues in effect. This Release may be signed in counterparts, including by electronic
signature, each of which is an original.


INDIVIDUAL                                COMPANY

[INDIVIDUAL NAME]                         [COMPANY NAME]


Signature: ________________________       By: _______________________________

Print name: _______________________       Name: _____________________________

Date: _____________________________       Title: ____________________________

Phone / email: ____________________       Date: _____________________________


PARENT OR GUARDIAN (required if the Individual is a minor)

[GUARDIAN NAME]


Signature: ________________________       Relationship to Individual: ______________

Print name: _______________________       Date: _____________________________
`,
  },
];

export default CONFIDENTIALITY_TEMPLATES;
